import { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from 'react';
import { DEAL_CURRENCY_FIELD_NAME } from '../../constants/associations';
import { loggingMiddleware } from '../../utils/store';
import { calculateProperties, createCrmObject, fetchDealAmountSettings, getCurrencies, getObjectCreatorProperties } from './functions';
import { AssociationModalPropsContext } from './PropsContext';
import { CALCULATE_PROPERTIES, CREATE_CRM_OBJECT, GET_CURRENCIES, GET_DEAL_AMOUNT_SETTINGS, GET_PROPERTIES, initialState, reducer, RESET_CRM_OBJECT_ASSOCIATIONS, RESET_FORM_ERROR, RESET_NEW_LINE_ITEMS, RESET_SEARCH_PROPERTIES, SAVE_PRODUCTS, SET_CRM_OBJECT_ASSOCIATIONS, SET_CURRENCY, SET_FIELD, SET_FORM_FIELDS, SET_LEGACY_DEAL_AMOUNT_CALCULATION } from './reducer';
import { jsx as _jsx } from "react/jsx-runtime";
export const AssociationModalContext = /*#__PURE__*/createContext(initialState);
export const AssociationModalContextProvider = ({
  children,
  hasLegacyDealAmountCalculation = false
}) => {
  const [associationModal, dispatchCTX] = useReducer(loggingMiddleware('associations modal')(reducer), Object.assign({}, initialState, {
    hasLegacyDealAmountCalculation
  }));
  const {
    newContactEmail,
    apiWrapper
  } = useContext(AssociationModalPropsContext);
  const asyncFuncArg = useMemo(() => ({
    dispatch: dispatchCTX,
    apiWrapper
  }), [apiWrapper]);
  const dispatch = useCallback(action => {
    switch (action.type) {
      case GET_DEAL_AMOUNT_SETTINGS:
        fetchDealAmountSettings(asyncFuncArg);
        break;
      case GET_CURRENCIES:
        getCurrencies(asyncFuncArg);
        break;
      case CREATE_CRM_OBJECT:
        createCrmObject(asyncFuncArg, action.payload);
        break;
      case GET_PROPERTIES:
        getObjectCreatorProperties(asyncFuncArg, action.payload);
        break;
      case CALCULATE_PROPERTIES:
        {
          calculateProperties(asyncFuncArg, action.payload);
          break;
        }
      default:
        {
          dispatchCTX(action);
        }
    }
  }, [asyncFuncArg]);
  const actions = useMemo(() => ({
    createCrmObject: data => dispatch({
      type: CREATE_CRM_OBJECT,
      payload: data
    }),
    setCurrency: currency => dispatch({
      type: SET_CURRENCY,
      payload: currency
    }),
    setFormFields: fields => dispatch({
      type: SET_FORM_FIELDS,
      payload: fields
    }),
    setField: data => dispatch({
      type: SET_FIELD,
      payload: data
    }),
    setCrmObjectAssociations: data => dispatch({
      type: SET_CRM_OBJECT_ASSOCIATIONS,
      payload: data
    }),
    saveProducts: products => dispatch({
      type: SAVE_PRODUCTS,
      payload: products
    }),
    calculateProperties: data => dispatch({
      type: CALCULATE_PROPERTIES,
      payload: data
    }),
    resetNewLineItems: () => dispatch({
      type: RESET_NEW_LINE_ITEMS
    }),
    getProperties: data => dispatch({
      type: GET_PROPERTIES,
      payload: data
    }),
    resetSearchProperties: () => dispatch({
      type: RESET_SEARCH_PROPERTIES
    }),
    getCurrencies: () => dispatch({
      type: GET_CURRENCIES
    }),
    getDealAmountSettings: () => dispatch({
      type: GET_DEAL_AMOUNT_SETTINGS
    }),
    resetCrmObjectAssociations: () => dispatch({
      type: RESET_CRM_OBJECT_ASSOCIATIONS
    }),
    resetFormError: () => dispatch({
      type: RESET_FORM_ERROR
    })
  }),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);
  const state = useMemo(() => Object.assign({}, associationModal, {
    dispatch,
    associationModalActions: actions
  }), [associationModal, dispatch, actions]);
  useEffect(() => {
    const newFields = associationModal.crmObjectSearchProperties.map(({
      name,
      required,
      fieldType
    }) => {
      const field = {
        name,
        required: required || false,
        valid: true,
        value: '',
        fieldType: fieldType,
        disabled: false,
        tooltip: ''
      };
      if (newContactEmail && name === 'email') {
        field.value = newContactEmail;
      }
      if (name === DEAL_CURRENCY_FIELD_NAME && associationModal.homeCurrency) {
        actions.setCurrency(associationModal.homeCurrency);
        field.value = associationModal.homeCurrency;
      }
      return field;
    });
    if (!associationModal.hasMulticurrencyActive) {
      actions.setFormFields(newFields.filter(field => field.name !== DEAL_CURRENCY_FIELD_NAME));
    } else {
      actions.setFormFields(newFields);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [associationModal.crmObjectSearchProperties, newContactEmail]);
  useEffect(() => {
    if (associationModal.hasLegacyDealAmountCalculation !== hasLegacyDealAmountCalculation) {
      dispatch({
        type: SET_LEGACY_DEAL_AMOUNT_CALCULATION,
        payload: hasLegacyDealAmountCalculation
      });
    }
  }, [hasLegacyDealAmountCalculation, dispatch, associationModal.hasLegacyDealAmountCalculation]);
  return /*#__PURE__*/_jsx(AssociationModalContext.Provider, {
    value: state,
    children: children
  });
};